import {EphemeralRealtimeService} from '../../../core/services/ephemeral-realtime.service';
import {AppearanceService} from '../../../core/services/appearance.service';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {FileUpload} from 'primeng/fileupload';
import {OverlayPanel} from 'primeng/overlaypanel';
import {FilesUploadEventInterface} from '../../interfaces/files-upload-event.interface';
import {RxFormBuilder, RxwebValidators} from '@rxweb/reactive-form-validators';
import {UntypedFormGroup} from '@angular/forms';
import {FileRemoveEventInterface} from '../../interfaces/file-remove-event.interface';
import {defer, forkJoin, merge, Observable, of, Subject} from 'rxjs';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {catchError, debounceTime, distinctUntilChanged, finalize, ignoreElements, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {MediaObjectRestService} from '../../../api/services/media-object.rest.service';
import {SocialInputSubmitInterface} from './social-input-submit.interface';
import {MediaObjectPipe} from '../../../core/pipes/media-object.pipe';
import {MediaTypeEnum} from '../../../core/enums/media-type.enum';
import {AbstractComponent} from "../../../core/components/abstract-component";
import {Editor} from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import {SuggestionProps} from "@tiptap/suggestion";
import {TranslateService} from "@ngx-translate/core";
import {MediaObjectInterface} from "../../../core/interfaces/media-object.interface";
import {ChatAttachmentRestService} from "../../../api/services/chat-attachment.rest.service";
import {ResponseNotificationService} from "../../../core/services/response-notification.service";
import {GlobalSearchRestService} from "../../../api/services/global-search.rest.service";
import {GlobalSearchItemApiModel, GlobalSearchResultApiModel} from "../../../api/models/global-search-result.api.model";
import {HashtagRestService} from "../../../api/services/hashtag.rest.service";
import {HashtagApiModel} from "../../../api/models/hashtag.api.model";
import {QueryParamsApiModel} from "../../../api/models/query-params-api.model";
import {FilterParameterApiModel} from "../../../api/models/filter-parameter.api.model";
import {ResultListApiModel} from "../../../api/models/result-list.api.model";
import {HashtagRoutingService} from "../../../core/services/hashtag-routing.service";

const CustomMention = Mention.extend({
  name: 'mention',
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => ({
          'data-id': attributes.id,
        }),
      },
      slug: {
        default: null,
        parseHTML: element => element.getAttribute('data-slug'),
        renderHTML: attributes => ({
          'data-slug': attributes.slug,
        }),
      },
      alias: {
        default: null,
        parseHTML: element => element.getAttribute('data-alias'),
        renderHTML: attributes => ({
          'data-alias': attributes.alias,
        }),
      },
      type: {
        default: null,
        parseHTML: element => element.getAttribute('data-type'),
        renderHTML: attributes => ({
          'data-type': attributes.type,
        }),
      },
      ownerSlug: {
        default: null,
        parseHTML: element => element.getAttribute('data-owner-slug'),
        renderHTML: attributes => ({
          'data-owner-slug': attributes.ownerSlug,
        }),
      },
    };
  },
  renderHTML({ node }) {
    const alias = node.attrs.alias || 'user';
    const type = node.attrs.type;
    const slug = node.attrs.slug || '';
    const ownerSlug = node.attrs.ownerSlug || '';
    const href = type === 'project'
      ? (slug ? (ownerSlug ? `/${ownerSlug}/garage/${slug}` : `/garage/${slug}`) : '#')
      : (slug ? `/${slug}/profile` : '#');
    return [
      'a',
      {
        class: 'mention',
        'data-type': type,
        'data-id': node.attrs.id,
        'data-slug': slug,
        'data-alias': node.attrs.alias,
        'data-owner-slug': ownerSlug,
        href,
        target: '_blank',
        // rel: 'noopener noreferrer nofollow',
      },
      `@${alias}`,
    ];
  },
  parseHTML() {
    return [
      {
        tag: 'a.mention',
        priority: 100, // must be > link extension
      },
    ];
  },
});

const CustomHashtag = Mention.extend({
  name: 'hashtag',
  addAttributes() {
    return {
      slug: {
        default: null,
        parseHTML: element => element.getAttribute('data-slug'),
        renderHTML: attributes => ({
          'data-slug': attributes.slug,
        }),
      },
      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => ({
          'data-label': attributes.label,
        }),
      },
    };
  },
  renderHTML({ node }) {
    const slug = node.attrs.slug || '';
    const label = node.attrs.label || slug;
    return [
      'a',
      {
        class: 'hashtag-link',
        'data-slug': slug,
        'data-label': label,
        href: slug ? `/tags/${slug}` : '#',
      },
      `#${label}`,
    ];
  },
  parseHTML() {
    return [
      {
        tag: 'a.hashtag-link',
        priority: 100,
      },
    ];
  },
});

interface SocialInputPreviewFile extends File {
  __wtgPreviewMeta?: {
    localId?: string;
  };
}

@Component({
  selector: 'app-social-input',
  templateUrl: './social-input.component.html',
  styleUrls: ['./social-input.component.scss']
})
export class SocialInputComponent extends AbstractComponent implements OnInit, AfterViewInit {
  private detachMentionOverlayScrollListener: (() => void) | null = null;
  private hasViewInitialized = false;
  private existingMediaObjectAMs: MediaObjectInterface[] = [];
  private newFileSequence = 0;
  private readonly loadedPreviewFiles = new WeakSet<File>();
  private readonly loadedPreviewMediaUrls = new Set<string>();

  get value(): string {
    return this._value;
  }

  @Input() set value(value: string) {
    this._value = value;
  }
  get files(): File[] {
    return this._files;
  }

  set files(value: File[]) {
    this._files = value;
  }
  get mediaProgress(): number {
    return this._mediaProgress;
  }

  @Input() set mediaProgress(value: number) {
    this._mediaProgress = value;
  }

  get chatInputWidth(): number {
    if (this.chatInput.nativeElement.clientWidth) {
      return this.chatInput.nativeElement.clientWidth - (0.75 * 16) - 135;
    }
    return 0;
  }

  get chatPreviewWidth(): number {
    if (this.preview.nativeElement.clientWidth) {
      return this.preview.nativeElement.clientWidth - (0.75 * 16);
    }
    return 0;
  }

  get editorPreviewWidth(): number {
    if (this.editor.view.dom.clientWidth) {
      const editorEl = this.editor.view.dom as HTMLElement;
      const paragraph = editorEl.querySelector('p');
      if (!paragraph) {
        return 0;
      }
      const width = paragraph.getBoundingClientRect().width;
      return width - (0.75 * 16);
    }
    return 0;
  }


  get parentDivWidth(): number {
    if (this.parentDiv.nativeElement.clientWidth) {
      return this.parentDiv.nativeElement.clientWidth - (0.75 * 16) - 135;
    }
    return 0;
  }

  mentionItems: any[] = [];
  private userMentionItems: any[] = [];
  private projectMentionItems: any[] = [];
  private hashtagMentionItems: any[] = [];
  private readonly mentionSearch$ = new Subject<{kind: 'mention'|'hashtag'; query: string}>();
  itemSize = 50;          // wysokość pojedynczego elementu
  scrollHeight = 50;     // początkowa wysokość
  private mentionRange: { from: number; to: number } | null = null;
  private activeSuggestionKind: 'mention'|'hashtag' = 'mention';
  editor!: Editor;
  users = ['theripper', 'garageking', 'carspot'];
  tags = ['#vintage', '#muscle', '#sport'];
  @ViewChild('mentionPanel') mentionPanelRef?: ElementRef<HTMLElement>;
  @ViewChild('emojiPanel') emojiPanel?: OverlayPanel;
  public isMentionLoading = false;
  public isMentionPanelVisible = false;
  public mentionPanelStyle: Record<string, string> = {};

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.assignWindowWidth();
    this.calculateHeight();
    this.realignMentionOverlay();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.hideMentionPanel();
      return;
    }

    const clickedInsideInput = !!target.closest('app-social-input');
    const clickedInsideMentionPanel = !!target.closest('.mention-panel-floating');
    if (!clickedInsideInput && !clickedInsideMentionPanel) {
      this.hideMentionPanel();
    }
  }
  updateScrollHeight() {
    const maxHeight = 360;
    this.scrollHeight = Math.min(this.mentionItems.length * 52 || 52, maxHeight);
  }

  constructor(
    private readonly ephemeralRealtime: EphemeralRealtimeService,
    private readonly fb: RxFormBuilder,
    private readonly renderer: Renderer2,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly globalSearchRS: GlobalSearchRestService,
    private readonly translate: TranslateService,
    private readonly chatAttachmentRS: ChatAttachmentRestService,
    private readonly rns: ResponseNotificationService,
    private readonly hashtagRS: HashtagRestService,
    private readonly hashtagRouting: HashtagRoutingService,
    public readonly appearance: AppearanceService,
  ) {
    super();
  }

  private _value: string = null;
  private _label = '';
  private currentPlaceholder = '';
  @Input() set label(value: string) {
    this._label = value || '';
    this.currentPlaceholder = this._label;
    this.refreshEditorPlaceholder();
  }
  get label(): string {
    return this._label;
  }
  @Input() public loading = false;
  @Input() public disabled = false;
  @Input() public maxEditorHeight = 300;
  @Input() public minEditorHeight = 43;
  @Input() public chatMode = false;
  @Input() public imageOnly = false;
  @Input() public typingChannel: string | null = null;
  private typingIdleTimer?: ReturnType<typeof setTimeout>;
  @Input() public allowEmptySubmit = false;
  @Input() public submitDisabled = false;
  @Input() public maxAttachmentSize = 10 * 1024 * 1024;
  private _files: File[] = [];
  public newFiles: File[] = [];
  @Output() public submitted: EventEmitter<SocialInputSubmitInterface> = new EventEmitter<SocialInputSubmitInterface>();
  @Output() public mediaUpload: EventEmitter<FilesUploadEventInterface> = new EventEmitter<FilesUploadEventInterface>();
  @Output() public mediaRemove: EventEmitter<SocialInputSubmitInterface> = new EventEmitter<SocialInputSubmitInterface>();
  @ViewChild('chatInput') chatInput: ElementRef;
  @ViewChild('preview') preview: ElementRef;
  @ViewChild(FileUpload) fileUpload: FileUpload;

  public windowWidth: number;
  public isMediaBoxHidden = true;
  public isFileUploadInitialized = false;
  public form: UntypedFormGroup;
  private isMultirow = false;
  get mediaObjectAMs(): MediaObjectInterface[] {
    return this.existingMediaObjectAMs;
  }

  @Input() set mediaObjectAMs(value: MediaObjectInterface[]) {
    this.existingMediaObjectAMs = this.normalizeMediaObjects(value || []);
    if (this.hasViewInitialized) {
      this.syncExistingMediaPreviewFiles();
    }
  }
  private _mediaProgress: number;

  public readonly MediaTypeEnum = MediaTypeEnum;

  ngOnInit() {
    this.isMediaBoxHidden = !this._files?.length && !this.mediaObjectAMs.length;
    this.windowWidth = window.innerWidth;
    this.form = this.fb.group(
      {
        content: [null, [
          RxwebValidators.required(),
          RxwebValidators.minLength({value: 1}),
          RxwebValidators.maxLength({value: 650000})
        ]],
      }
    );
    this.form.patchValue({content: this.value});
    this.subscriptions.push(this.mentionSearch$.pipe(
      debounceTime(150),
      distinctUntilChanged((prev, current) => prev?.kind === current?.kind && prev?.query === current?.query),
      switchMap(({kind, query}) => {
        if (kind === 'hashtag') {
          const params = new QueryParamsApiModel();
          params.filters.push(new FilterParameterApiModel('name', query || '', 'single'));
          return this.hashtagRS.getCollection(params).pipe(
            map((result) => ({kind, result})),
            catchError(() => of({kind, result: new ResultListApiModel<HashtagApiModel>(HashtagApiModel)}))
          );
        }

        return this.globalSearchRS.search({
          q: query || '',
          types: ['project', 'user'],
          limit: 100,
          groupByType: true,
          highlight: false,
          forceRefresh: true,
        }).pipe(
          map((result) => ({kind, result})),
          catchError(() => of({kind, result: {total: 0, items: [], groups: {}} as GlobalSearchResultApiModel}))
        );
      })
    ).subscribe(({kind, result}: {kind: 'mention'|'hashtag'; result: GlobalSearchResultApiModel | ResultListApiModel<HashtagApiModel>}) => {
      if (kind === 'hashtag') {
        const hashtagResult = result as ResultListApiModel<HashtagApiModel>;
        this.hashtagMentionItems = (hashtagResult?.records || [])
          .map((item) => this.mapHashtagItem(item))
          .filter((item): item is any => !!item);
        this.userMentionItems = [];
        this.projectMentionItems = [];
      } else {
        const globalResult = result as GlobalSearchResultApiModel;
        const projectItems = globalResult?.groups?.project || globalResult?.items?.filter((item) => item?.type === 'project') || [];
        const userItems = globalResult?.groups?.user || globalResult?.items?.filter((item) => item?.type === 'user') || [];

        this.projectMentionItems = projectItems
          .map((item: GlobalSearchItemApiModel) => this.mapGlobalSearchProjectItem(item))
          .filter((item): item is any => !!item);
        this.userMentionItems = userItems
          .map((item: GlobalSearchItemApiModel) => this.mapGlobalSearchUserItem(item))
          .filter((item): item is any => !!item);
        this.hashtagMentionItems = [];
      }

      this.mergeMentionItems();
      this.isMentionLoading = false;
      this.queueMentionOverlayRealign();
    }));
    this.initEditor();
  }

  ngAfterViewInit(): void {
    this.hasViewInitialized = true;
    this.editor.commands.setContent(this.value);
    this.enforceEditorMaxHeight();

    this.syncExistingMediaPreviewFiles();
    this.fileUpload?.onSelect.subscribe(() => {
      this.openMediaBox();
    });

    this.updateScrollHeight();
    this.bindMentionOverlayScrollListener();
  }

  override ngOnDestroy(): void {
    clearTimeout(this.typingIdleTimer);
    if (this.typingChannel) this.ephemeralRealtime.publishTyping(this.typingChannel, false);
    this.unbindMentionOverlayScrollListener();
    if (this.editor && !this.editor.isDestroyed) {
      this.editor.destroy();
    }
    super.ngOnDestroy();
  }

  public openMediaBox(): void {
    this.isFileUploadInitialized = true;
    setTimeout(() => {
      const elements = document.querySelectorAll('.p-fileupload-row button');
      elements.forEach((e) => {
        this.renderer.addClass(e, 'p-button-text');
      });
      this.isMediaBoxHidden = false;
    }, 1);
  }

  onInputSubmitClick(options: {allowEmptyContent?: boolean} = {}): void {
    const allowEmptyContent = options.allowEmptyContent ?? this.allowEmptySubmit;
    const content = this.imageOnly && !this.getEditorText().length ? '' : this.getSubmitContent();
    const hasText = this.getEditorText().length > 0;
    const hasAttachments = this._files?.length > 0 || this.mediaObjectAMs?.length > 0;
    if (this.submitDisabled) {
      return;
    }
    if (!hasText && !hasAttachments && (!allowEmptyContent || !this.chatMode)) {
      return;
    }
    if (!this.chatMode && !this.form.valid && !(allowEmptyContent && !hasText && hasAttachments)) {
      return;
    }
    this.loading = true;
    this.disabled = true;
    const uploadCandidateFiles = this.getUploadCandidateFiles();
    this.addMedia(uploadCandidateFiles).subscribe({
      next: (mediaObjectAMs: MediaObjectInterface[]) => {
        this.existingMediaObjectAMs = this.normalizeMediaObjects([...this.mediaObjectAMs, ...mediaObjectAMs]);
        this.closeMediaLoadProcess();
        this.newFiles = [];
        if (uploadCandidateFiles.length) {
          this.fileUpload?.clear();
          this.syncExistingMediaPreviewFiles();
        }
        if (this.typingChannel) this.ephemeralRealtime.publishTyping(this.typingChannel, false);
        this.submitted.emit({mediaObjectAMs: this.mediaObjectAMs, content, input: this});
      },
      error: () => this.restoreAfterFailedSubmit()
    });
  }

  public isSubmitActionDisabled(): boolean {
    if (this.loading || this.disabled || this.submitDisabled) {
      return true;
    }

    if (!this.chatMode) {
      const hasAttachments = this.files.length > 0 || this.mediaObjectAMs.length > 0;
      return (this.form?.invalid ?? true) && !(this.allowEmptySubmit && hasAttachments && !this.getEditorText().length);
    }

    if (this.allowEmptySubmit) {
      return false;
    }

    const hasText = this.getEditorText().length > 0;
    const hasAttachments = this._files?.length > 0 || this.mediaObjectAMs?.length > 0;

    return !hasText && !hasAttachments;
  }

  onKeydown(event, chatInput): void {
    this.form.patchValue({content: chatInput.value});
    if (window.innerWidth > 768) {
      if (event.keyCode === 13 && !event.shiftKey) {
        // this.onInputSubmitClick(chatInput);
        event.preventDefault();
      }
    }
  }

  onTextareaResize(): void {
    if (!this.isMultirow && this.chatPreviewWidth > this.chatInputWidth) {
      this.renderer.addClass(this.chatInput.nativeElement, 'input-multi-row');
      this.renderer.removeClass(this.chatInput.nativeElement, 'input-single-row');
      this.isMultirow = true;
    }

    if (this.isMultirow && this.chatPreviewWidth < this.chatInputWidth) {
      this.renderer.removeClass(this.chatInput.nativeElement, 'input-multi-row');
      this.renderer.addClass(this.chatInput.nativeElement, 'input-single-row');
      this.isMultirow = false;
    }
  }

  onEditorInput(): void {
    if (this.typingChannel) {
      this.ephemeralRealtime.publishTyping(this.typingChannel, this.getEditorText().length > 0);
      clearTimeout(this.typingIdleTimer);
      const channel = this.typingChannel;
      this.typingIdleTimer = setTimeout(() => this.ephemeralRealtime.publishTyping(channel, false), 2000);
    }
    if (!this.isMultirow && this.editorPreviewWidth > this.parentDivWidth) {
      this.renderer.addClass(this.editor.view.dom, 'input-multi-row');
      this.renderer.removeClass(this.editor.view.dom, 'input-single-row');
      this.isMultirow = true;
    }

    if (this.isMultirow && this.editorPreviewWidth < this.parentDivWidth) {
      this.renderer.removeClass(this.editor.view.dom, 'input-multi-row');
      this.renderer.addClass(this.editor.view.dom, 'input-single-row');
      this.isMultirow = false;
    }

    this.enforceEditorMaxHeight();
    this.realignMentionOverlay();
  }

  onEditorKeydown(event): void {
    this.form.patchValue({content: this.getEditorContentForForm()});
    if (window.innerWidth > 768) {
      if (event.keyCode === 13 && !event.shiftKey) {
        this.onInputSubmitClick();
        event.preventDefault();
      }
    }
  }

  public onMediaBoxOpen(): void {
    this.fileUpload?.choose();
  }

  public onFileSelect(event: FilesUploadEventInterface): void {
    event.files.forEach((file: File) => {
      if (this.imageOnly && !['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'].includes(file.type)) {
        this.rns.emitError(this.translate.instant('chat.attachments.invalid_type'));
        const index = this.fileUpload?.files.indexOf(file) ?? -1;
        if (index >= 0) {
          this.fileUpload.remove(null, index);
        }
        return;
      }
      const typedFile = this.decorateNewUploadFile(file);

      if (this.chatMode) {
        if (file.size > this.maxAttachmentSize) {
          this.rns.emitError(this.translate.instant('chat.attachments.too_large'));
          return;
        }
        if (!this.getAttachmentType(file)) {
          this.rns.emitError(this.translate.instant('chat.attachments.invalid_type'));
          return;
        }
      }
      if (!this.files.find((existingFile) => this.isSameFile(existingFile, file))) {
        this.files.push(typedFile);
      }
      if (!this.newFiles.find((existingFile) => this.isSameFile(existingFile, file))) {
        this.newFiles.push(typedFile);
      }
    });
    this.mediaUpload.emit(event);
  }

  public closeMediaLoadProcess(): void {
    this.mediaProgress = null;
  }

  onFileRemove(event: FileRemoveEventInterface): void {
    this.files = this.files.filter(e => !this.isSameFileInstance(e, event.file));
    this.newFiles = this.newFiles.filter(e => !this.isSameFileInstance(e, event.file));
    this.isMediaBoxHidden = !this.existingMediaObjectAMs.length && !this.files.length;
    this.closeMediaLoadProcess();
    this.mediaRemove.emit({mediaObjectAMs: this.mediaObjectAMs, content: this.form.get('content')?.value || ''});
    this.disabled = false;
  }

  public removeExistingMedia(mediaObject: MediaObjectInterface): void {
    const key = this.getMediaObjectKey(mediaObject);
    this.existingMediaObjectAMs = this.existingMediaObjectAMs.filter((item) => this.getMediaObjectKey(item) !== key);
    this.isMediaBoxHidden = !this.existingMediaObjectAMs.length && !this.files.length;
    this.mediaRemove.emit({mediaObjectAMs: this.mediaObjectAMs, content: this.form.get('content')?.value || ''});
  }

  public getExistingMediaPreviewUrl(mediaObject: MediaObjectInterface): string {
    return MediaObjectPipe.transformToLink(mediaObject, {defaultImageType: 'loading'});
  }

  public isExistingMediaPreviewLoaded(mediaObject: MediaObjectInterface): boolean {
    const url = this.getExistingMediaPreviewUrl(mediaObject);
    return !!url && this.loadedPreviewMediaUrls.has(url);
  }

  public markExistingMediaPreviewLoaded(mediaObject: MediaObjectInterface): void {
    const url = this.getExistingMediaPreviewUrl(mediaObject);
    if (url) {
      this.loadedPreviewMediaUrls.add(url);
    }
  }

  public isNewFilePreviewLoaded(file: File): boolean {
    return this.loadedPreviewFiles.has(file);
  }

  public markNewFilePreviewLoaded(file: File): void {
    this.loadedPreviewFiles.add(file);
  }

  addMedia(files: File[]): Observable<any> {
    this.disabled = true;
    const requests: Observable<MediaObjectInterface>[] = [];
    files.forEach((file: File) => {
      const upload$ = this.uploadAttachment(file);
      if (upload$) {
        requests.push(upload$);
      }
    });

    if (!requests.length) {
      return of([]);
    }

    return defer(() => {
      let counter = 0;
      const percent$ = new Subject();

      const modifiedObservablesList = requests.map(
        (item, index) => item.pipe(
          finalize(() => {
            const percentValue = ++counter * 100 / requests.length;
            percent$.next(percentValue);
          })
        )
      );

      const finalResult$ = forkJoin(modifiedObservablesList).pipe(
        tap(() => {
            percent$.next(100);
            percent$.complete();
          }
        ));

      return of([finalResult$, percent$.asObservable()]);
    }).pipe(
      mergeMap(([finalResult, progress]) => merge(
        progress.pipe(
          tap((value: number) => this.mediaProgress = value),
          ignoreElements()
        ),
        finalResult
      ))
    );
  }

  public postMediaObject(file): Observable<MediaObjectApiModel> {
    switch (this.getTypeByMimeTypes(file)) {
      case MediaTypeEnum.IMAGE:
        return this.mediaObjectRS.uploadImage(file);
      case MediaTypeEnum.VIDEO:
        return this.mediaObjectRS.uploadVideo(file);
      default:
        return this.mediaObjectRS.uploadImage(file);
    }
  }

  public deleteMediaObject(mediaObjectAM: MediaObjectInterface): Observable<MediaObjectApiModel> {
    if (this.chatMode) {
      return of(mediaObjectAM as MediaObjectApiModel);
    }
    return this.mediaObjectRS.deleteMediaObject(mediaObjectAM['@id']).pipe(map(() => mediaObjectAM as MediaObjectApiModel));
  }

  public reset(): void {
    this.editor.commands.clearContent();
    this.enforceEditorMaxHeight();
    this.fileUpload.clear();
    this.isMediaBoxHidden = true;
    this.loading = false;
    this.disabled = false;
    this.existingMediaObjectAMs = [];
    this.mediaObjectAMs = [];
    this.files = [];
    this.newFiles = [];
    this.newFileSequence = 0;
  }

  public restoreAfterFailedSubmit(): void {
    this.loading = false;
    this.disabled = false;
  }

  private syncExistingMediaPreviewFiles(): void {
    this.files = [...this.newFiles];
    if (this.fileUpload) {
      this.fileUpload.files = this.files;
    }
    this.isMediaBoxHidden = !this.mediaObjectAMs.length && !this.files.length;
  }

  private getUploadCandidateFiles(): File[] {
    const uniqueFiles = new Map<string, File>();

    this.newFiles.forEach((file) => {
      const signature = this.getNewFileSignature(file);
      if (!uniqueFiles.has(signature)) {
        uniqueFiles.set(signature, file);
      }
    });

    return Array.from(uniqueFiles.values());
  }

  private normalizeMediaObjects(mediaObjects: MediaObjectInterface[]): MediaObjectInterface[] {
    const unique = new Map<string, MediaObjectInterface>();
    mediaObjects.forEach((mediaObject) => {
      const key = this.getMediaObjectKey(mediaObject);
      if (key !== null && !unique.has(key)) {
        unique.set(key, mediaObject);
      }
    });

    return Array.from(unique.values());
  }

  private getMediaObjectKey(mediaObject: MediaObjectInterface | null | undefined): string | null {
    if (!mediaObject) {
      return null;
    }

    return mediaObject['@id']
      || mediaObject['id']?.toString?.()
      || mediaObject.originalName
      || mediaObject.name
      || null;
  }

  private isSameFile(left: File, right: File): boolean {
    return left.name === right.name
      && left.size === right.size
      && left.lastModified === right.lastModified;
  }

  private decorateNewUploadFile(file: File): SocialInputPreviewFile {
    const previewFile = file as SocialInputPreviewFile;
    previewFile.__wtgPreviewMeta = {
      ...(previewFile.__wtgPreviewMeta || {}),
      localId: previewFile.__wtgPreviewMeta?.localId || `new:${++this.newFileSequence}`,
    };

    return previewFile;
  }

  private isSameFileInstance(left: File, right: File): boolean {
    const leftId = this.getLocalFileId(left as SocialInputPreviewFile);
    const rightId = this.getLocalFileId(right as SocialInputPreviewFile);

    if (leftId && rightId) {
      return leftId === rightId;
    }

    return left === right || this.isSameFile(left, right);
  }

  private getNewFileSignature(file: File): string {
    return `${file.name}-${file.size}-${file.type}-${file.lastModified}`;
  }

  private getLocalFileId(file: SocialInputPreviewFile): string | null {
    return file.__wtgPreviewMeta?.localId || null;
  }

  public getTypeByMimeTypes(file: File): string  {
    return file.type.includes(MediaTypeEnum.IMAGE.toLowerCase()) ? MediaTypeEnum.IMAGE :
      file.type.includes(MediaTypeEnum.VIDEO.toLowerCase()) ? MediaTypeEnum.VIDEO : null;
  }

  togglePlay(video: any): void {
    if (video.paused) {
        video.play();
    } else {
        video.pause();
    }
  }

  removeFile(file: File, event: Event) {
    const index = this.fileUpload.files.indexOf(file);
    this.fileUpload.remove(event, index);
  }

  onMentionSearch(term: string, kind: 'mention'|'hashtag' = 'mention') {
    this.activeSuggestionKind = kind;
    this.isMentionLoading = true;
    this.mentionSearch$.next({kind, query: (term || '').trim()});
  }


  initEditor(): void {
    this.editor = new Editor({
      editorProps: {
        attributes: {
          class: 'ProseMirror input-single-row w-100 write-message p-inputtext p-inputtextarea', // <--- tutaj Twoje klasy bazowe
          spellcheck: 'false',
        },
      },
      extensions: [
        StarterKit,
        Image,
        Placeholder.configure({
          placeholder: () => this.currentPlaceholder,
        }),
        CustomMention.configure({
          deleteTriggerWithBackspace: true,
          HTMLAttributes: {
            class: 'mention',
          },
          suggestions: [{
            char: '@',
            items: ({query}: {query: string, editor: Editor}) => {
              this.onMentionSearch(query, 'mention');
              return [];
            },
            render: () => {
              return {
                onStart: (props: SuggestionProps) => {
                  this.mentionRange = props.range;
                  this.isMentionPanelVisible = true;
                  this.queueMentionOverlayRealign();
                },
                onUpdate: props => {
                  this.mentionRange = props.range;
                  this.queueMentionOverlayRealign();
                },
                // onKeyDown: (props, event) => {
                //   // opcjonalnie obsługa strzałek i enter
                // },
                onExit: () => {
                  this.mentionRange = null;
                  this.hideMentionPanel();
                },
              };
            }
          }],
        }),
        CustomHashtag.configure({
          deleteTriggerWithBackspace: true,
          HTMLAttributes: {
            class: 'hashtag-link',
          },
          suggestions: [{
            char: '#',
            items: ({query}: {query: string, editor: Editor}) => {
              this.onMentionSearch(query, 'hashtag');
              return [];
            },
            render: () => {
              return {
                onStart: (props: SuggestionProps) => {
                  this.activeSuggestionKind = 'hashtag';
                  this.mentionRange = props.range;
                  this.isMentionPanelVisible = true;
                  this.queueMentionOverlayRealign();
                },
                onUpdate: props => {
                  this.activeSuggestionKind = 'hashtag';
                  this.mentionRange = props.range;
                  this.queueMentionOverlayRealign();
                },
                onExit: () => {
                  this.mentionRange = null;
                  this.hideMentionPanel();
                },
              };
            }
          }],
        }),
      ],
    });

    this.editor.on('blur', () => { if (this.typingChannel) this.ephemeralRealtime.publishTyping(this.typingChannel, false); });
    this.editor.on('transaction', ({ editor }) => {
      if (editor.isFocused) {
        const content = this.getEditorContentForForm();
        this.form.get('content').setValue(content, { emitEvent: false });
      }
      this.enforceEditorMaxHeight();
    });
  }

  selectItem(item: any) {
    if (item?.type === 'hashtag') {
      const slug = this.hashtagRouting.normalizeSlug(item?.slug || item?.label || item?.alias);
      const label = this.hashtagRouting.normalizeSlug(item?.label || item?.alias || slug);
      this.editor
        .chain()
        .focus()
        .deleteRange(this.mentionRange)
        .insertContent([
          {
            type: 'hashtag',
            attrs: {
              slug,
              label,
            },
          },
          { type: 'text', text: '\u00A0' },
        ])
        .focus('end')
        .run();

      this.mentionRange = null;
      this.hideMentionPanel();
      return;
    }

    const mentionType = item?.type === 'project' ? 'project' : 'profile';
    this.editor
      .chain()
      .focus()
      .deleteRange(this.mentionRange)
      .insertContent([
        {
          type: 'mention',
          attrs: {
            id: item.id,
            alias: item.alias,
            slug: item.slug,
            type: mentionType,
            ownerSlug: item.ownerSlug || null,
          },
        },
        { type: 'text', text: '\u00A0' },
      ])
      .focus('end')
      .run();

    this.mentionRange = null;
    this.hideMentionPanel();
  }

  selectEmoji(item: string) {
    this.editor
      .chain()
      .focus()
      .insertContent(`${item} `)
      .run();
    this.emojiPanel?.hide();
  }

  toggleEmojiPicker(event: Event): void {
    event.stopPropagation();
    this.emojiPanel?.toggle(event);
  }

  private getCaretCoords() {
    const { from } = this.editor.state.selection; // pozycja kursora
     // zwraca { left, right, top, bottom }
    return this.editor.view.coordsAtPos(from);
  }

  private getAttachmentType(file: File): string | null {
    if (file.type === 'application/pdf') {
      return 'DOCUMENT';
    }
    return this.getTypeByMimeTypes(file);
  }

  private uploadAttachment(file: File): Observable<MediaObjectInterface> | null {
    if (this.chatMode) {
      const type = this.getAttachmentType(file);
      if (!type) {
        return null;
      }
      return this.chatAttachmentRS.uploadAttachment(file, type);
    }
    return this.postMediaObject(file);
  }

  private getEditorContentForForm(): string {
    return this.editor.getHTML();
  }

  private getSubmitContent(): string {
    return this.editor.getHTML();
  }

  private getEditorText(): string {
    return this.editor.getText().trim();
  }

  private enforceEditorMaxHeight(): void {
    const editorElement = this.editor?.view?.dom as HTMLElement | undefined;
    if (!editorElement) {
      return;
    }

    const minHeight = Math.max(43, Number(this.minEditorHeight) || 43);
    const maxHeight = Math.max(minHeight, Number(this.maxEditorHeight) || 300);
    editorElement.style.minHeight = `${minHeight}px`;
    editorElement.style.maxHeight = `${maxHeight}px`;
    editorElement.style.overflowY = 'auto';
    editorElement.style.overflowX = 'hidden';

    editorElement.style.height = 'auto';
    const nextHeight = Math.min(maxHeight, Math.max(minHeight, editorElement.scrollHeight || minHeight));
    editorElement.style.height = `${nextHeight}px`;
  }

  private refreshEditorPlaceholder(): void {
    if (!this.editor) {
      return;
    }

    // Trigger decoration refresh in Placeholder extension after runtime label update.
    this.editor.view.dispatch(this.editor.state.tr);
  }

  private mergeMentionItems(): void {
    const all = this.activeSuggestionKind === 'hashtag'
      ? [...this.hashtagMentionItems]
      : [...this.userMentionItems, ...this.projectMentionItems];
    const deduplicated = new Map<string, any>();
    all.forEach((item) => {
      const key = `${item?.type || 'user'}:${item?.id || item?.alias}`;
      if (!deduplicated.has(key)) {
        deduplicated.set(key, item);
      }
    });
    this.mentionItems = Array.from(deduplicated.values());
    this.updateScrollHeight();
    if (this.mentionItems.length) {
      this.isMentionLoading = false;
    }
  }

  public get projectMentionDisplayItems(): any[] {
    return this.mentionItems.filter((item) => item?.type === 'project');
  }

  public get userMentionDisplayItems(): any[] {
    return this.mentionItems.filter((item) => item?.type === 'user');
  }

  public get hashtagMentionDisplayItems(): any[] {
    return this.mentionItems.filter((item) => item?.type === 'hashtag');
  }

  private mapGlobalSearchProjectItem(item: GlobalSearchItemApiModel): any | null {
    const renderData = item?.renderData || {};
    const projectSlug = item?.slug || this.extractProjectSlugFromUri(item?.uri);
    const ownerSlug = renderData?.ownerSlug || this.extractOwnerSlugFromUri(item?.uri);
    if (!projectSlug) {
      return null;
    }
    const projectAlias = renderData?.alias || item?.title || projectSlug;
    const ownerUsername = ownerSlug ? (renderData?.owner || '') : '';
    const vehicleIdentity = renderData?.vehicleIdentity || item?.subtitle || '';
    return {
      id: item.id,
      alias: ownerUsername ? `${projectAlias} | ${ownerUsername}` : projectAlias,
      slug: projectSlug,
      ownerSlug,
      ownerUsername,
      projectAlias,
      vehicleIdentity,
      thumbnail: item.thumbnail || null,
      type: 'project',
    };
  }

  private mapGlobalSearchUserItem(item: GlobalSearchItemApiModel): any | null {
    const slug = item?.slug || this.extractUserSlugFromUri(item?.uri);
    const alias = item?.subtitle || item?.title || item?.label || '';
    if (!slug || !alias) {
      return null;
    }

    return {
      id: item.id,
      alias,
      slug,
      uri: item?.uri || `/${slug}/profile`,
      thumbnail: item?.thumbnail || null,
      type: 'user',
    };
  }

  private mapHashtagItem(item: HashtagApiModel): any | null {
    const slug = this.hashtagRouting.normalizeSlug(item?.name);
    if (!slug) {
      return null;
    }

    return {
      id: item.id,
      alias: `#${slug}`,
      slug,
      label: slug,
      type: 'hashtag',
    };
  }

  private extractOwnerSlugFromUri(uri?: string | null): string | null {
    const match = (uri || '').match(/^\/([^/]+)\/garage\/([^/]+)$/);

    return match?.[1] || null;
  }

  private extractProjectSlugFromUri(uri?: string | null): string | null {
    const ownerRouteMatch = (uri || '').match(/^\/([^/]+)\/garage\/([^/]+)$/);
    if (ownerRouteMatch?.[2]) {
      return ownerRouteMatch[2];
    }
    const aliasRouteMatch = (uri || '').match(/^\/garage\/([^/]+)$/);

    return aliasRouteMatch?.[1] || null;
  }

  private extractUserSlugFromUri(uri?: string | null): string | null {
    const match = (uri || '').match(/^\/([^/]+)(?:\/profile)?$/);

    return match?.[1] || null;
  }

  private bindMentionOverlayScrollListener(): void {
    if (typeof window === 'undefined' || this.detachMentionOverlayScrollListener) {
      return;
    }

    const handler = () => this.queueMentionOverlayRealign();
    window.addEventListener('scroll', handler, true);
    this.detachMentionOverlayScrollListener = () => window.removeEventListener('scroll', handler, true);
  }

  private unbindMentionOverlayScrollListener(): void {
    this.detachMentionOverlayScrollListener?.();
    this.detachMentionOverlayScrollListener = null;
  }

  private queueMentionOverlayRealign(): void {
    const ref = requestAnimationFrame(() => this.realignMentionOverlay());
    this.animationFrameIds.push(ref);
  }

  private realignMentionOverlay(): void {
    if (!this.isMentionPanelVisible) {
      return;
    }

    const inputHost = this.parentDiv?.nativeElement as HTMLElement | undefined;
    if (!inputHost) {
      return;
    }

    const inputRect = inputHost.getBoundingClientRect();
    const suggestionTarget = this.resolveMentionSuggestionTarget();
    const visualViewport = window.visualViewport;
    const viewportLeft = visualViewport?.offsetLeft ?? 0;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportWidth = visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportBottom = viewportTop + viewportHeight;
    const panelWidth = Math.min(340, Math.max(260, viewportWidth - 24));
    const panelHeight = this.resolveMentionPanelHeight();
    const availableBelow = Math.max(120, viewportBottom - inputRect.bottom - 14);
    const availableAbove = Math.max(120, inputRect.top - viewportTop - 14);
    const belowTop = inputRect.bottom + 6;
    const preferredAboveTop = inputRect.top - panelHeight - 6;
    const shouldPlaceAbove = belowTop + panelHeight > viewportBottom - 8 && availableAbove > availableBelow;
    const top = shouldPlaceAbove
      ? Math.max(viewportTop + 8, inputRect.top - Math.min(panelHeight, availableAbove) - 6)
      : belowTop;
    const maxHeight = shouldPlaceAbove ? availableAbove : availableBelow;
    const rawLeft = suggestionTarget
      ? suggestionTarget.getBoundingClientRect().left
      : inputRect.left + 12;
    const maxLeft = Math.max(viewportLeft + 12, viewportLeft + viewportWidth - panelWidth - 12);
    const left = Math.min(Math.max(viewportLeft + 12, rawLeft), maxLeft);

    this.mentionPanelStyle = {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      width: `${panelWidth}px`,
      maxHeight: `${Math.max(120, maxHeight)}px`,
      zIndex: '6001',
    };
  }

  private resolveMentionSuggestionTarget(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const suggestion = document.querySelector('.suggestion');
    if (suggestion instanceof HTMLElement) {
      return suggestion;
    }

    return null;
  }

  private resolveMentionPanelHeight(): number {
    const renderedHeight = this.mentionPanelRef?.nativeElement?.getBoundingClientRect().height || 0;
    if (renderedHeight > 0) {
      return renderedHeight;
    }

    if (this.isMentionLoading) {
      return 88;
    }

    if (!this.mentionItems.length) {
      return 76;
    }

    return Math.min(376, Math.max(120, this.scrollHeight + 24));
  }

  private hideMentionPanel(): void {
    this.isMentionPanelVisible = false;
    this.mentionPanelStyle = {};
  }
}
